import { EngagementGroup } from "engagement-type-templates/models";
import { UpcomingReviewNotice } from "scheduling";
import { User } from "users";
import { Activity } from "activities/models";
import { ChecklistCompletion } from "checklists";
import { AssignedPdCourse, Decision } from "decisions";
import { LevelOfAdjustment } from "level-of-adjustment/models";
import { CommitteeMeeting, CommitteeMeetingTabledReview } from "../committee-meetings";
import { FirmResponse } from "../firm-response/models";
import { Exemption } from "../exemptions";
import { Ina } from "../inas";
import { PprpProgram, PprpProgramDetails } from "../pprp";
import { Monitoring } from "monitoring/models";
import { DocType } from "common/DocType";
import { RegistrationCommitteeItem } from "registration-committee-items";

export interface FirmEntityType {
  typeCode: number;
  typeShortName: string;
  typeDescription: string;
}

export interface FirmSatelliteOffice {
  id: number;
  entityNumber: number;
  name: string;
  address: string;
  city: string;
  practiceReviewContactName: string;
  practiceReviewContactPhone: string | null;
  practiceReviewContactEmail: string | null;
  includeWithMainOffice: boolean;
}

export interface Firm {
  id: number;
  name: string;
  entityNumber: number;
  firmStatus: string | null;
  subStatus: string | null;
  entityType: FirmEntityType;

  practiceLocale: string | null;
  zone: string | null;
  pprpSubStatus: string | null;
  isCpab: boolean;

  address1: string;
  address2: string | null;
  city: string;
  province: string | null;
  country: string | null;
  postalCode: string | null;
  phoneNumber: string | null;
  faxNumber: string | null;

  designatedPartnerFirstName: string | null;
  designatedPartnerLastName: string | null;
  designatedPartnerTitle: string | null;
  designatedPartnerDesignation: string | null;

  hoDesignatedPartnerFirstName: string | null;
  hoDesignatedPartnerLastName: string | null;
  hoDesignatedPartnerTitle: string | null;
  hoDesignatedPartnerDesignation: string | null;
  hoDesignatedPartnerEmail: string | null;

  latestPartners: FirmPartner[];
  latestClienteleProfile: ClienteleProfile | null;

  satelliteOffices: FirmSatelliteOffice[];

  lastRefreshedFromDataSource: string;

  practiceReviews: PracticeReview[];
  latestPracticeReview: PracticeReview;

  historicalPrs: HistoricalPr[];
}

export interface FirmPartner {
  id: number;
  reviewYear: number;
  name: string;
  designation: string;
  yearDesignationObtained: number;
  isOther: boolean;
  withFirmSince: number;
  function: string;
  compilation: boolean;
  audit: boolean;
  review: boolean;
}

export interface ClienteleProfile {
  firmId: number;
  reviewYear: number;

  publicCompaniesHours: number;
  publicCompaniesClients: number;
  offeringDocumentsHours: number;
  offeringDocumentsClients: number;
  otherEntitiesUnderIfrsHours: number;
  otherEntitiesUnderIfrsClients: number;
  notForProfitHours: number;
  notForProfitClients: number;
  publicSectorHours: number;
  publicSectorClients: number;
  otherAuditHours: number;
  otherAuditClients: number;

  reviewYearEndHours: number;
  reviewYearEndClients: number;
  reviewEntitiesUnderIfrsHours: number;
  reviewEntitiesUnderIfrsClients: number;
  reviewInterimHours: number;
  reviewInterimClients: number;

  businessValuationsHours: number;
  businessValuationsClients: number;
  insolvencyHours: number;
  insolvencyClients: number;
  trustAccountingHours: number;
  trustAccountingClients: number;
  compilationHours: number;
  compilationClients: number;
  handlingOfTrustFundsHours: number;
  handlingOfTrustFundsClients: number;
  accountingServicesHours: number;
  accountingServicesClients: number;
  taxServicesHours: number;
  taxServicesClients: number;
  forensicHours: number;
  forensicClients: number;
  otherServicesHours: number;
  otherServicesClients: number;

  totalHours: number;
  totalClients: number;
}

export interface FirmProfile {
  firmId: number;
  reviewYear: number;

  fullTimePartners: number;
  designatedAccountantsFt: number;
  designatedAccountantsPt: number;
  candidatesFt: number;
  candidatesPt: number;
  otherFt: number;
  otherPt: number;
}

export enum PRBaseStatusCode {
  Any = "Any",
  InProgress = "InProgress",
  Hold = "Hold",
  ExemptionPending = "ExemptionPending",
  Exempt = "Exempt",
  Removed = "Removed",
  Completed = "Completed"
}

export interface PRStatus {
  id: number;
  baseStatusCode: PRBaseStatusCode;
  statusName: string;
}

export enum PRPhaseCode {
  Any = 0,
  Scheduling = 1,
  PerformPracticeReview = 2,
  DirectorApproval = 3,
  CommitteeMeeting = 4,
  Completed = 5
}

export interface PRPhase {
  id: PRPhaseCode;
  name: string;
}

export interface PREstimate {
  id: number;
  date: string;
  isFinalized: boolean;

  compilationTotal: number;
  otherQamTotal: number;
  finalizeReviewTotal: number;
  totalSystemEstimate: number;
  overrideEstimate: number | null;
  finalEstimate: number;

  notes: string | null;

  partners: PRPartnerEstimate[];
}

export interface PRPartnerEstimate {
  id: number;
  firmPartnerId: number;
  timeEstimate: number;
}

export interface AttachedDocument {
  id: number;
  type: DocType;
  typeAsString: string;
  typeFriendlyName: string;
  fileName: string;
  url: string;
  portalSigneeName: string | null;
  portalDeclarationDate: string | null;
  isExcludedFromRegComBinder: boolean;
}

export interface ClientToReview {
  id: number;
  clientName: string;
}

export interface EngagementType {
  id: number;
  name: string;
  engagementGroup: EngagementGroup;
}

export interface ReviewedClient {
  id: number;
  name: string;
  partnerName: string;
  refNum: string;
  engagementTypeId: number;
  engagementType: EngagementType;
  reviewedByUserId: number;
  reviewer: User | null;
  checklistCompletion: ChecklistCompletion;

  clientFile: ClientFile | null;
  candidate: Candidate | null;
}

export interface ClientFile {
  businessNature: string;
  fiscalYearEnd: string;
  cpabReview: boolean;
  competitiveBid: boolean;
  isHoursNotFees: boolean;
  feesOrHoursAmount: number;
  materiality: number | null;
  assets: number;
  liabilities: number;
  revenue: number;
  netIncomeBeforeTaxes: number;
  reportDate: string | null;
}

export interface Candidate {
  idNo: string;
  perEffectiveDate: string | null;
  monthsExperience: number | null;
  mentorReviews: number | null;
  requiredNoOfReviews: number | null;
  rotation1: string | null;
  rotation1Duration: number | null;
  rotation2: string | null;
  rotation2Duration: number | null;
  rotation3: string | null;
  rotation3Duration: number | null;
  rotation4: string | null;
  rotation4Duration: number | null;
  reasonableAuditChargeableHours: boolean;
  reasonableReviewChargeableHours: boolean;
  reasonableOtherAssuranceChargeableHours: boolean;
  reasonableCompilationChargeableHours: boolean;
  reasonableTaxationChargeableHours: boolean;
  reasonableOtherChargeableHours: boolean;
  additionalComments: string | null;
}

export interface ReviewedClientInput {
  id?: number;
  practiceReviewId: number;
  name: string;
  partnerName: string;
  engagementTypeId: number;
  refNum: string;
  reviewedByUserId: number;
  clientFile: ClientFile | null;
  candidate: Candidate | null;
}

export interface EngagementType {
  id: number;
  name: string;
  engagementGroup: EngagementGroup;
}

export interface TimeEntry {
  id: number;
  enteredDate: string;
  enteredHours: number;
  isAdmin: boolean;
  user: User;
  practiceReview: PracticeReview;
  __typename?: string;
}
export interface TimeEntryInput {
  id?: number;
  enteredDate: string;
  enteredHours: number;
  userId: number;
  practiceReviewId: number;
}

export interface PracticeReview {
  id: number;
  prNumber: string;
  prNumberPrefix: number;
  prStage: number;
  reviewYear: number;
  datesHaveBeenConfirmed: boolean;
  startDate: string;
  endDate: string;
  completedDate: string | null;
  initialDueDate: string | null;
  isNonAssurance: boolean;
  isDeskReview: boolean;
  isPprpProgramReview: boolean;
  pprpProgramReviewRegComItems: RegistrationCommitteeItem[] | null;
  bestPprpProgramReviewRegComItem: RegistrationCommitteeItem | null;
  reviewType: "Non-assurance" | "Assurance" | "PPRP Program";
  hasIncreasedRisk: boolean;

  contactName: string;
  contactEmail: string;
  contactEmailUri: string;
  hasValidContactEmail: boolean;
  contactPhone: string;
  contactTitle: string;
  contactDesignation: string;

  firm: Firm;
  status: PRStatus;
  phase: PRPhase;

  requiresSchedulingQuestionnaire: boolean;
  schedulingQuestionnaireUrl: string | null;
  isMissingSchedulingQuestionnaire: boolean;
  isSchedulingQuestionnaireReviewedByLead: boolean;

  requiresTaxQuestionnaire: boolean;
  taxQuestionnaireUrl: string | null;
  isMissingTaxQuestionnaire: boolean;
  isTaxQuestionnaireReviewedByLead: boolean;

  requiresConfidentialClientList: boolean;
  confidentialClientListUrl: string | null;
  isMissingConfidentialClientList: boolean;

  requiresSelfEvaluationQuestionnaire: boolean;
  selfEvaluationQuestionnaireUrl: string | null;
  isMissingSelfEvaluationQuestionnaire: boolean;
  isSelfEvaluationQuestionnaireReviewedByLead: boolean;

  deficiencyReportUrl: string | null;
  practiceReviewReportUrl: string | null;
  checklistNotesReportUrl: string | null;

  firmProfileForReviewYear: FirmProfile;

  hasPaperFiles: boolean;
  hasScannableFiles: boolean;
  hasElectronicFiles: boolean;
  hasTaxOnlyFiles: boolean;

  hasBeenEstimated: boolean;
  estimate: PREstimate;
  mostRecentRescheduleReason: string | null;

  pprpProgram: PprpProgram | null;

  previousReview?: PracticeReview;

  prNotes: string | null;
  notesFromSchedulingPhase: string | null;

  leadReviewer: Reviewer | null;
  otherReviewers: Reviewer[];

  actualTimeToDate: number | null;
  timeEntries: TimeEntry[];

  upcomingReviewNotice: UpcomingReviewNotice | null;

  committeeMeeting: CommitteeMeeting | null;
  committeeMeetingIsConfirmed: boolean;

  attachedDocuments: AttachedDocument[];
  sharePointUrl: string;

  clientsToReview: ClientToReview[];

  reviewedClients: ReviewedClient[];

  engagementTypes: EngagementType[];

  firmChecklistCompletion: ChecklistCompletion;

  held: {
    heldBy: string;
    reason: string;
    date: string;
  } | null;

  levelOfAdjustment: LevelOfAdjustment;

  activities: Activity[];
  inas: Ina[];

  finalReportPackageUrl: string | null;
  isFinalReportReviewedByCoordinator: boolean;
  isFinalReportReviewedByDirector: boolean;

  hasDecisionLetter: boolean;
  decisionLetterUrl: string | null;

  pdDeclarationFormUrl: string | null;
  pdProofOfAttendanceFormsUrl: string | null;

  decisions: Decision[];
  mostAuthoritativeDecision: Decision | null;

  isDeficiencyReportPosted: boolean;

  hasBeenReturned: boolean;
  isReturnedComplete: boolean;
  reasonReturnedIncomplete: string | null;

  invoiceAmount: number | null;
  invoiceGst: number | null;
  invoiceTotal: number | null;
  invoiceIsFinal: boolean | null;

  requiresResponse: boolean;
  firmResponse: FirmResponse;

  committeeTab: string | null;
  tabledFromPrevious: CommitteeMeetingTabledReview | null;
  tabledToNext: CommitteeMeetingTabledReview | null;
  mostRecentlyTabled: CommitteeMeetingTabledReview | null;

  exemption: Exemption | null;
  exemptionLetterUrl: string | null;
  signedExemptionLetterUrl: string | null;

  directedPDNotes: string | null;
  isDirectedPdCompleted: boolean;
  directedPdCompletedByUser: User | null;
  directedPdCompletedDate: string | null;

  pprpProgramDetails: PprpProgramDetails | null;

  monitoringFrequencyInDays: number | null;
  monitoringOccurrencesRemaining: number | null;
  monitoringNotes: string | null;
  monitoringCompleted: boolean | null;

  monitorings: Monitoring[];
  latestMonitoring: Monitoring | null;
  lastCompletedMonitoring: Monitoring | null;
}

export interface HistoryItem {
  __typename?: string;
  id?: number;
  isComply: boolean | null;
  nonComplyFinancial: boolean;
  nonComplyAudit: boolean;
  nonComplyReview: boolean;
  nonComplyQualityManagement: boolean;
  nonComplyTax: boolean;
  nonComplyCompilation: boolean;
  nonComplyOther: boolean;
  nonComplyOtherSpecified: string | null;
}

export interface HistoricalPr extends HistoryItem {
  reviewYear: number;
  prNumber: string;
  otherComments: string | null;
}

export interface DataGridHistoryItem extends HistoryItem {
  _gridId: string;
}

export function isDecision(historyItem: HistoryItem): historyItem is Decision {
  return historyItem.__typename === "Decision";
}

export function isHistoricalPr(historyItem: HistoryItem): historyItem is HistoricalPr {
  return historyItem.__typename === "HistoricalPr";
}

export interface ImportPracticeReviewResults {
  new: number;
  updated: number;
  failed: number;
}

export interface Reviewer {
  practiceReviewId: number;
  userId: number;
  isLead: boolean;
  reviewedClientsSubmitted: boolean;
  reviewedClientsHaveBeenUnsubmitted: boolean;
  reviewerNotes: string;
  resubmissionNotes: string;
  user: User;
  clients: ReviewedClient[];
}

export interface PrReportParameters {
  sortOrder: SortOrderForClientFiles;
  filterFromReferenceLetter: string | null;
  filterToReferenceLetter: string | null;
  filterClientId: number | null;
  filterPartnerName: string | null;
  filterReviewerUserId: number | null;
  filterEngagementTypeId: number | null;
  includeDecision: boolean;
}

export enum SortOrderForClientFiles {
  ClientName = "ClientName",
  Partner = "Partner",
  EngagementType = "EngagementType",
  Reviewer = "Reviewer",
  ReferenceLetter = "ReferenceLetter",
  EngagementGroup = "EngagementGroup"
}

export interface PracticeReviewSearchResults {
  searchResults: PracticeReview[];
  searchResultsCount: number;
}

export interface UploadDocumentResponse extends Partial<PracticeReview> {
  attachedDocuments: AttachedDocument[];
  assignedPd?: AssignedPdCourse | null;
}
